import { Injectable } from '@angular/core';

import { Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap, tap } from 'rxjs/operators';

import {
  ContainerState,
  ContainerStateHelper,
  PaginationData,
  PaginationRange,
  SnackbarConfig,
  TiimeSnackbarService
} from 'tiime-material';

import { DocumentApiService } from '@api-services/document-api.service';
import { ApiTableDataStoreBase, ApiTableDataStoreState } from '@bases/table-data-store/api-table-data-store.base';
import { Document } from '@models/document';
import { DocumentCategory } from '@models/document-category';
import { UploadDocumentDialogData } from '@modules/shared/dialogs/upload-document-dialog/upload-document-dialog-data';
import { DocumentViewService } from '@modules/shared/services/document-view/document-view.service';
import { ConfirmDialogData } from '@shared-dialogs/confirm-dialog/confirm-dialog-data';
import { ConfirmDialogService } from '@shared-dialogs/confirm-dialog/confirm-dialog.service';
import { UploadDocumentDialogService } from '@shared-dialogs/upload-document-dialog/upload-document-dialog.service';
import { UPLOAD_FILE_CONFIG } from '@shared-overlays/upload-file-overlay/upload-file-config.constant';

export interface DocumentsFilters {
  category: number;
}

export interface DocumentsStoreState extends ApiTableDataStoreState<Document, DocumentsFilters> {
  documentsCategories: DocumentCategory[];
}

export const initialState: DocumentsStoreState = {
  data: [],
  containerState: ContainerState.contentPlaceholder,
  pagination: PaginationRange.withPageSize(100),
  filters: {
    category: null
  },
  search: null,
  sort: null,
  isLoading: false,
  documentsCategories: [],
  selected: null
};

@Injectable()
export class DocumentsStore extends ApiTableDataStoreBase<Document, DocumentsFilters, DocumentsStoreState> {
  readonly documentsCategories$: Observable<DocumentCategory[]> = this.select(
    ({ documentsCategories }) => documentsCategories
  );

  readonly setDocumentCategories = this.updater((state, documentsCategories: DocumentCategory[]) => ({
    ...state,
    documentsCategories
  }));

  readonly loadDocumentCategories = this.effect(documents$ =>
    documents$.pipe(
      switchMap(() => this.documentApiService.getDocumentCategories()),
      tap(this.setDocumentCategories)
    )
  );
  readonly acceptedTypes = UPLOAD_FILE_CONFIG.extendedAcceptedFileTypes;
  readonly maximumSize = UPLOAD_FILE_CONFIG.maximumSize;

  private readonly getPreviousPageFromViewService = this.effect((_$: Observable<unknown>) =>
    _$.pipe(tap(() => this.setPagination(this.get().pagination.previous())))
  );

  private readonly getNextPageFromViewService = this.effect((_$: Observable<unknown>) =>
    _$.pipe(tap(() => this.setPagination(this.get().pagination.next())))
  );

  constructor(
    private readonly documentViewService: DocumentViewService,
    private readonly documentApiService: DocumentApiService,
    private readonly confirmDialogService: ConfirmDialogService,
    private readonly snackbar: TiimeSnackbarService,
    private readonly uploadDocumentDialogService: UploadDocumentDialogService
  ) {
    super(initialState);

    this.getPreviousPageFromViewService(this.documentViewService.previousPage$);
    this.getNextPageFromViewService(this.documentViewService.nextPage$);
  }

  deleteDocument(documentId: number): void {
    this.confirmDialogService
      .open({
        data: new ConfirmDialogData(
          'Attention',
          `Êtes-vous sûr de vouloir supprimer ce document ?`,
          'Supprimer',
          'Annuler',
          null,
          'warn'
        )
      })
      .afterClosed()
      .pipe(
        filter(confirm => !!confirm),
        switchMap(() => this.documentApiService.deleteDocument(documentId)),
        tap(() => this.snackbar.open(`Le document a bien été supprimé`, SnackbarConfig.success)),
        tap(() => this.forceRefresh())
      )
      .subscribe();
  }

  openUploadDocumentDialog(selectedDocuments?: FileList): void {
    const { filters, documentsCategories } = this.get();
    if ([...(selectedDocuments ?? [])].some((file: File) => !this.acceptedTypes.includes(file.type))) {
      this.snackbar.openError("Certains documents sélectionnés ne sont pas d'un type autorisé");
      return;
    }

    const data: UploadDocumentDialogData = {
      selectedDocumentCategory: documentsCategories.find(documentCategory => documentCategory.id === filters.category),
      selectedDocuments
    };

    this.uploadDocumentDialogService
      .open({
        data
      })
      .afterClosed()
      .pipe(
        filter((documentCategoryCreated: boolean) => documentCategoryCreated),
        tap(() => this.loadDocumentCategories())
      )
      .subscribe();
  }

  protected override getData(): Observable<Document[]> {
    const { pagination, sort, search, filters } = this.get();
    const expand = 'file_infos';

    return this.documentApiService
      .getDocuments(pagination, search, sort, { categoryId: filters.category }, expand)
      .pipe(
        tap((paginationData: PaginationData<Document>) =>
          this.patchState({
            data: paginationData.data,
            pagination: paginationData.paginationRange,
            containerState: ContainerStateHelper.getContainerState(
              paginationData.data.length,
              !search && !this.hasActiveFilters() && paginationData.paginationRange.min === 0
            ),
            isLoading: false
          })
        ),
        tap((paginationData: PaginationData<Document>) => {
          this.documentViewService.setPaginationState({
            firstPage: paginationData.paginationRange.isFirstPage,
            currentPageData: paginationData.data.map(({ id }: Document) => id),
            lastPage: paginationData.paginationRange.count !== '*'
          });
        }),
        map(({ data }) => data),
        catchError(() => of([]))
      );
  }

  private hasActiveFilters(): boolean {
    const filters = this.get().filters;

    return !!filters.category;
  }
}
